import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  TextField,
  InputAdornment,
  makeStyles
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLastSearchAccountQuery,
  setLastSelectedAccount
} from 'src/redux/slices/storedValues';
import SearchDialog from './SearchDialog';
import useAccSearch from '../hooks/useAccSearch';
import SearchIcon from '@material-ui/icons/Search';
import useNotif from 'src/hooks/useNotif';

const useStyles = makeStyles(theme => ({
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  adornStart: {
    paddingLeft: '1px'
  }
}));

const SrchAcc = ({
  selectedAcc = {},
  onSelectAccount = () => null,
  error = false,
  errorMessage = '',
  indent = false,
  caption,
  size,
  allowSelectParent = true,
  required = false,
  accountTypes = [],
  specificTypes = [],
  expenseClass = [],
  view = null,
  showModal = false,
  callBackCloseModal = () => null,
  style = {},
  forceOpenDialog = false,
  showSearchField = true,
  endAdornment = null,
  lib = false,
  showExport = false,
  transactionKey = null,
  searchFieldRef = null,
  onFieldKeyDown: onFieldKeyDownProp = null,
  ...rest
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showInactive, setShowInactive] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const lastScrollPositionRef = useRef(0);
  const {
    loading,
    accountList,
    accountTypes: accTypes,
    query,
    exportAccounts,
    refresh,
    setQuery,
    setAccountTypes,
    setExpenseClass
  } = useAccSearch({ lib, showInactive });

  const { lastAccountSearchQuery, lastSelectedAccount } = useSelector(
    data => data.storedValues
  );

  const effectiveTransactionKey = transactionKey || 'global';
  const scopedLastSelectedAccount =
    lastSelectedAccount?.[effectiveTransactionKey] || null;

  const [open, setOpen] = useState(false);
  const notify = useNotif();

  const handleSrchAcc = e => {
    const value = e.target.value;

    if (value.trim() === '') {
      setQuery(lastAccountSearchQuery.trim());
      setOpen(true);
      return;
    }

    if (value !== '' || typeof value !== 'undefined' || value !== null) {
      const lastChar = value[value.length - 1];
      setQuery(lastChar);
    }

    setOpen(true);
  };

  const handleChangeQuery = event => {
    setQuery(event.target.value);
  };

  const handleScrollPositionChange = position => {
    setScrollPosition(position);
    lastScrollPositionRef.current = position; // Always persist latest scroll position
  };

  const handleAccSelect = item => {
    if (item.lft + 1 !== item.rgt && allowSelectParent === false) {
      notify.warning('Please select non-parent account.');
    } else {
      setOpen(false);
      callBackCloseModal(false);
      dispatch(setLastSearchAccountQuery(query));
      dispatch(
        setLastSelectedAccount({
          key: effectiveTransactionKey,
          account: item
        })
      );
      onSelectAccount({
        ...item,
        jeItemIndex: rest.index
      });
    }
  };

  const handleChangeAccountTypesFilter = e => {
    const { name, checked } = e.target;
    const accType = +name;

    setAccountTypes(prev => {
      if (!checked) {
        return prev.filter(prev => prev !== accType);
      }

      return [...prev, accType];
    });
  };

  const handleClose = () => {
    setOpen(false);
    callBackCloseModal(false);
  };

  useEffect(() => {
    if (accountTypes.length) setAccountTypes(accountTypes);
  }, [accountTypes]);

  useEffect(() => {
    if (expenseClass.length) setExpenseClass(expenseClass);
  }, [expenseClass]);

  useEffect(() => {
    setOpen(showModal);
    if (showModal) {
      setScrollPosition(lastScrollPositionRef.current); // Restore scroll position on open
    }
  }, [showModal]);

  if (!selectedAcc) return null;

  const handleQuickSelectFromField = () => {
    if (!(rest?.isEditable ?? true)) return;

    const hasSelectedAcc = Boolean(selectedAcc?.id);

    if (
      !hasSelectedAcc &&
      scopedLastSelectedAccount &&
      scopedLastSelectedAccount.ixAcc
    ) {
      // Auto-fill last selected account
      onSelectAccount({
        ...scopedLastSelectedAccount,
        jeItemIndex: rest.index
      });
    }
  };

  const handleFieldKeyDown = e => {
    if (typeof onFieldKeyDownProp === 'function') {
      onFieldKeyDownProp(e);
      if (e.defaultPrevented) return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuickSelectFromField();
    }
  };

  return (
    <>
      <SearchDialog
        open={forceOpenDialog || open}
        accounts={accountList}
        query={query}
        loading={loading}
        onChangeQuery={handleChangeQuery}
        onSelectAccount={handleAccSelect}
        onClose={handleClose}
        refresh={refresh}
        accountTypes={accTypes}
        exportAccounts={exportAccounts}
        showExport={showExport}
        onChangeAccountTypes={handleChangeAccountTypesFilter}
        showInactive={showInactive}
        setShowInactive={setShowInactive}
        scrollPosition={scrollPosition}
        onScrollPositionChange={handleScrollPositionChange}
      />

      {showSearchField && (
        <>
          {Boolean(view) ? (
            view
          ) : (
            <TextField
              fullWidth
              name="SrchAcc"
              value={selectedAcc.title}
              disabled={!(rest?.isEditable ?? true)}
              InputProps={{
                style: { paddingLeft: indent ? '1em' : '' },
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="search"
                      onClick={() => setOpen(true)}
                      disabled={!(rest?.isEditable ?? true)}
                      size={size ?? 'medium'}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: endAdornment,
                classes: {
                  adornedStart: classes.adornStart
                }
              }}
              error={error}
              helperText={errorMessage}
              placeholder="Search..."
              variant="outlined"
              label={caption ?? 'Select Account'}
              onChange={handleSrchAcc}
              onKeyDown={handleFieldKeyDown}
              inputRef={searchFieldRef}
              size={size ?? 'medium'}
              required={required}
              style={style}
            />
          )}
        </>
      )}
    </>
  );
};

export default SrchAcc;
